import { useEffect, useMemo, useState } from "react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { api } from "@/lib/api";
import { SLOTS, to12h } from "@/lib/slots";
import { ChevronLeft, ChevronRight } from "lucide-react";

function monthMatrix(year, month) {
  const first = new Date(year, month, 1);
  const startDay = first.getDay();
  const days = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= days; d++) cells.push(new Date(year, month, d));
  return cells;
}

const fmt = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function PublicCalendar() {
  const now = new Date();
  const [cursor, setCursor] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const [booked, setBooked] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [selected, setSelected] = useState(fmt(now));

  useEffect(() => {
    api.get("/appointments/slots").then((r) => setBooked(r.data)).catch(() => setBooked([]));
    api.get("/blocks").then((r) => setBlocks(r.data)).catch(() => setBlocks([]));
  }, []);

  const cells = useMemo(() => monthMatrix(cursor.y, cursor.m), [cursor]);
  const bookedByDate = useMemo(() => {
    const map = {};
    booked.forEach((b) => {
      map[b.appointment_date] = map[b.appointment_date] || new Set();
      map[b.appointment_date].add(b.appointment_time);
    });
    return map;
  }, [booked]);

  const closedDays = useMemo(() => new Set(blocks.filter((b) => b.all_day).map((b) => b.date)), [blocks]);
  const blockedByDate = useMemo(() => {
    const map = {};
    blocks.filter((b) => !b.all_day && b.time).forEach((b) => {
      map[b.date] = map[b.date] || new Set();
      map[b.date].add(b.time);
    });
    return map;
  }, [blocks]);

  const monthName = new Date(cursor.y, cursor.m).toLocaleString("default", { month: "long", year: "numeric" });
  const shift = (n) => {
    let m = cursor.m + n, y = cursor.y;
    if (m < 0) { m = 11; y--; } if (m > 11) { m = 0; y++; }
    setCursor({ y, m });
  };
  const selectedTaken = bookedByDate[selected] || new Set();
  const selectedBlocked = blockedByDate[selected] || new Set();
  const selectedClosed = closedDays.has(selected);

  return (
    <div className="App">
      <Navbar />
      <section className="max-w-5xl mx-auto px-6 pt-32 pb-24">
        <p className="label text-sm uppercase tracking-[0.15em] text-[#78698A] font-semibold">Availability</p>
        <h1 className="font-serif text-5xl text-[#2D2336] mt-3">Appointment Calendar</h1>
        <p className="text-lg text-[#5C5166] mt-4">
          Grey, struck-through times are already reserved. Patient details are private —
          only availability is shown here.
        </p>

        <div className="mt-10 grid lg:grid-cols-2 gap-10">
          <div className="bg-white border border-[#E5DEEB] rounded-2xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <button onClick={() => shift(-1)} data-testid="cal-prev" className="p-2 rounded-full hover:bg-[#F3EFF7]"><ChevronLeft className="h-5 w-5 text-[#6B4C8A]" /></button>
              <h3 className="font-serif text-2xl text-[#2D2336]">{monthName}</h3>
              <button onClick={() => shift(1)} data-testid="cal-next" className="p-2 rounded-full hover:bg-[#F3EFF7]"><ChevronRight className="h-5 w-5 text-[#6B4C8A]" /></button>
            </div>
            <div className="grid grid-cols-7 text-center text-xs font-semibold text-[#78698A] mb-2">
              {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => <div key={i}>{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {cells.map((d, i) => {
                if (!d) return <div key={i} />;
                const ds = fmt(d);
                const has = bookedByDate[ds];
                const isSel = ds === selected;
                return (
                  <button key={i} onClick={() => setSelected(ds)} data-testid={`cal-day-${ds}`}
                    className={`aspect-square rounded-lg text-sm font-medium flex flex-col items-center justify-center transition-colors ${
                      isSel ? "bg-[#6B4C8A] text-white" : "hover:bg-[#F3EFF7] text-[#2D2336]"
                    }`}>
                    {d.getDate()}
                    {has && <span className={`h-1.5 w-1.5 rounded-full mt-0.5 ${isSel ? "bg-white" : "bg-[#C8B3D6]"}`} />}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="bg-white border border-[#E5DEEB] rounded-2xl p-6 shadow-sm">
            <h3 className="font-serif text-2xl text-[#2D2336]">
              {new Date(selected + "T00:00:00").toLocaleDateString("default", { weekday: "long", month: "long", day: "numeric" })}
            </h3>
            <p className="text-sm text-[#78698A] mt-1">
              {selectedClosed ? "Closed this day" : `${selectedTaken.size + selectedBlocked.size} unavailable of ${SLOTS.length} slots`}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-5" data-testid="availability-slots">
              {SLOTS.map((s) => {
                const isTaken = selectedClosed || selectedTaken.has(s) || selectedBlocked.has(s);
                return (
                  <div key={s} className={`py-2.5 rounded-full text-sm font-medium text-center border ${
                    isTaken ? "bg-[#F1EEF4] text-[#B0A6BC] border-[#E5DEEB] line-through" : "bg-[#F5FBF7] text-[#3E7B5A] border-[#CFE7D8]"
                  }`}>
                    {to12h(s)}
                  </div>
                );
              })}
            </div>
            <a href="/book" className="inline-block mt-6 bg-[#6B4C8A] text-white px-6 py-3 rounded-full font-medium hover:bg-[#543C6E] transition-colors">
              Book an open time →
            </a>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
